import { useEffect, useState } from 'react'
import CIMSFormItem from 'components/CIMSFormItem'
import { CIMSModalWrapperProps } from 'components/CIMSModal/types'
import { useUsersTableContext } from '../../../../../../UsersTableContext'
import { Form, Switch } from 'antd'
import FormlessModal from 'components/CIMSModal/FormlessModal'
import { useForm, useWatch } from 'antd/lib/form/Form'
import { useEditUserMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useGlobalState } from 'context/GlobalStateContext'
import { HelpMessage } from 'utils/constants'
import { errorHandler } from 'hooks/errorHandler'
import { User } from '../../../../../../UsersTableContext/types'
import { EditUserFormValues } from './types'
import { getInitialValues, tooltip } from './utils'
import { useAuthContext } from 'context/AuthContext'
import { useDisabledOptions } from 'hooks/useDisabledOptions'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import CIMSSelect from 'components/CIMSSelect'
import PermissionEditor from '../../../../../../../PermissionEditor'

const EditUserModal: React.FC<CIMSModalWrapperProps<User>> = ({
  visible,
  onClose,
  state,
}) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { user } = useAuthContext()
  const { refetch, roles } = useUsersTableContext()
  const [form] = useForm<EditUserFormValues>()
  const [editing, setEditing] = useState(false)
  const [editUser] = useEditUserMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editUser: {
        user: { username },
      },
    }) => simpleEditNotification(username),
  })
  const watchAdmin = useWatch('admin', form)
  const rolesWatch = useWatch('roles', form)
  const options = useOptions({
    items: roles,
    label: 'name',
    value: 'id',
    tooltip,
    sort: 'name',
  })
  const [disabled, { some }] = useDisabledOptions([
    { reason: 'loading', disabled: editing },
    {
      disabled: watchAdmin === true,
      reason: { why: 'disabled', message: 'Project Admins have full access' },
    },
  ])
  useEffect(() => {
    form.setFieldsValue(getInitialValues(state))
  }, [state, form])
  const help = HelpMessage.Administration.Users
  return (
    <FormlessModal
      title={`Edit ${state?.username}`}
      height="tall"
      visible={visible}
      cancel={{
        fn: () => onClose(),
      }}
      ok={{
        fn: () => form.submit(),
        loading: editing,
        props: { disabled: state?.developer },
      }}
    >
      <Form
        form={form}
        initialValues={getInitialValues(state)}
        onFinish={async ({ permissionIds, admin, ...values }) => {
          const rolePermissionIds = [
            ...new Set(
              values.roles.flatMap(
                ({ value }) =>
                  roles.find((role) => role.id === value)?.permissions ?? []
              )
            ),
          ]

          if (!state?.id) {
            return errorHandler('User id must be defined')
          }

          if (!projectId) {
            return errorHandler('Project id must be defined')
          }

          setEditing(true)
          await editUser({
            variables: {
              editUserInput: {
                id: state?.id,
                projectId,
                admin,
                roleIds: values.roles.map(({ value }) => value.toString()),
                additionalPermissionIds: permissionIds.filter(
                  (id) => !rolePermissionIds.includes(id)
                ),
              },
            },
          })
          setEditing(false)
          refetch()
          onClose()
        }}
      >
        {user?.admin(projectId).authorized ? (
          <CIMSFormItem
            label="Project Admin"
            name="admin"
            help={help.ProjectAdmins}
            valuePropName="checked"
          >
            <Switch />
          </CIMSFormItem>
        ) : null}
        <CIMSFormItem
          label="Roles"
          name="roles"
          help={help.Roles}
          disabled={disabled}
        >
          <CIMSSelect
            multiple
            disabled={some}
            loading={editing}
            options={options}
          />
        </CIMSFormItem>
        <CIMSFormItem name="permissionIds" disabled={disabled}>
          <PermissionEditor
            loading={editing}
            help={help.Permissions}
            roles={roles.filter(({ id }) =>
              rolesWatch?.some(({ value }) => id === value)
            )}
            disabled={some}
          />
        </CIMSFormItem>
      </Form>
    </FormlessModal>
  )
}

export default EditUserModal
